import { useEffect, useRef, useState, type ReactNode } from 'react';
import { LoaderCircle, Pencil } from 'lucide-react';
import { Chargement, MessageErreur } from '../ui/Primitives';
import { useToast } from '../ui/useToast';
import { ErreurApi } from '../../lib/api';
import { horodatageReunion } from '../../lib/emargement-affichage';
import { tempsLisible } from '../../lib/temps-video';
import { useCorrigerTranscription } from '../../lib/useCours';
import { useTranscriptionLecon, type Replique, type VueLecon, type VueTranscription } from '../../lib/useLecteur';

/** Après un geste de la personne dans la liste, elle n'est plus déroulée par la lecture pendant ce temps. */
const PAUSE_SUIVI_MS = 4000;

const CLASSE_BOUTON =
  'inline-flex min-h-[36px] items-center gap-1.5 rounded-lg px-3 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 [@media(pointer:coarse)]:min-h-[44px]';

/** Le texte tel que l'API le gardera : espaces resserrés. */
function normaliser(texte: string): string {
  return texte.replace(/\s+/g, ' ').trim();
}

/** Réplique lue à `secondes` : celle qui la contient, s'il y en a une. */
function repliqueCourante(repliques: readonly Replique[], secondes: number): number | null {
  const ms = secondes * 1000;
  for (let rang = repliques.length - 1; rang >= 0; rang -= 1) {
    const replique = repliques[rang]!;
    if (replique.debutMs <= ms) return ms < Math.max(replique.finMs, replique.debutMs + 1) ? rang : null;
  }
  return null;
}

function origine(transcription: VueTranscription): string {
  const source =
    transcription.source === 'TRANSCRIPTION' ? 'Générée automatiquement' : 'Sous-titres déposés par le formateur';
  return transcription.corrigeeLe ? `${source} · corrigée le ${horodatageReunion(transcription.corrigeeLe)}` : source;
}

function Encart({ children }: { children: ReactNode }): JSX.Element {
  return <div className="rounded-2xl bg-gray-50 p-4 text-sm leading-relaxed text-codialis-gray sm:p-5">{children}</div>;
}

/**
 * Onglet « Transcription » du lecteur (TF-445) : le texte horodaté de la
 * leçon — celui des sous-titres —, une ligne par réplique. Un clic envoie la
 * vidéo au passage ; la réplique lue est surlignée et gardée visible dans la
 * liste, sans faire défiler la page. En aperçu, l'auteur et la direction
 * corrigent le texte, réplique par réplique, en réécoutant le passage.
 */
export function TranscriptionLecon({
  vue,
  temps,
  allerA,
}: {
  vue: VueLecon;
  /** Position de lecture, en secondes. */
  temps: number;
  /** Envoie la vidéo au passage ; `null` sans vidéo. */
  allerA: ((secondes: number) => void) | null;
}): JSX.Element {
  const toast = useToast();
  const { data: transcription, isLoading, error, refetch } = useTranscriptionLecon(vue.lecon.id, true);
  const corriger = useCorrigerTranscription(vue.cours.id, vue.lecon.id);
  const [brouillon, setBrouillon] = useState<string[] | null>(null);
  const [erreurCorrection, setErreurCorrection] = useState<string | null>(null);
  const liste = useRef<HTMLOListElement>(null);
  const derniereInteraction = useRef(0);

  const repliques = transcription?.repliques ?? [];
  const courante = allerA ? repliqueCourante(repliques, temps) : null;

  // La réplique lue reste visible dans la liste : seule la liste défile.
  useEffect(() => {
    const conteneur = liste.current;
    if (courante === null || brouillon || !conteneur) return;
    if (Date.now() - derniereInteraction.current < PAUSE_SUIVI_MS) return;
    const ligne = conteneur.children[courante] as HTMLElement | undefined;
    if (!ligne) return;
    const haut = ligne.offsetTop;
    const bas = haut + ligne.offsetHeight;
    if (haut < conteneur.scrollTop || bas > conteneur.scrollTop + conteneur.clientHeight) {
      conteneur.scrollTo({ top: Math.max(0, haut - conteneur.clientHeight / 3), behavior: 'smooth' });
    }
  }, [courante, brouillon]);

  if (isLoading) return <Chargement libelle="Chargement de la transcription…" />;
  if (error || !transcription) {
    return <MessageErreur message={error instanceof ErreurApi ? error.message : 'La transcription n’a pas pu être chargée.'} />;
  }

  const apercu = vue.acces === 'APERCU';
  if (transcription.etat === 'EN_PREPARATION') {
    return (
      <Encart>
        <p className="flex items-center gap-2">
          <LoaderCircle className="h-4 w-4 shrink-0 animate-spin text-codialis-sky" aria-hidden />
          La transcription de la vidéo est en préparation : elle s’affichera ici dès qu’elle sera prête.
        </p>
      </Encart>
    );
  }
  if (transcription.etat === 'AUCUNE') {
    return (
      <Encart>
        {apercu
          ? 'Cette leçon n’a pas encore de transcription : elle est produite au dépôt de la vidéo, ou reprise d’un fichier de sous-titres, à l’étape « Contenus & supports ».'
          : 'Cette leçon n’a pas de transcription.'}
      </Encart>
    );
  }

  const modifiees = brouillon ? brouillon.filter((texte, rang) => normaliser(texte) !== repliques[rang]?.texte).length : 0;

  const commencer = () => {
    setErreurCorrection(null);
    setBrouillon(repliques.map((replique) => replique.texte));
  };

  const enregistrer = async () => {
    if (!brouillon || !transcription.version) return;
    setErreurCorrection(null);
    try {
      await corriger.mutateAsync({ version: transcription.version, textes: brouillon });
      setBrouillon(null);
      toast('Transcription corrigée · sous-titres mis à jour');
    } catch (cause) {
      setErreurCorrection(cause instanceof ErreurApi ? cause.message : 'Les corrections n’ont pas pu être enregistrées.');
    }
  };

  const recharger = async () => {
    setBrouillon(null);
    setErreurCorrection(null);
    await refetch();
  };

  const noterInteraction = () => {
    derniereInteraction.current = Date.now();
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <p className="min-w-0 flex-1 basis-60 text-sm text-codialis-gray">
          {origine(transcription)}
          {allerA && ' · Cliquez sur une ligne pour aller à ce moment'}
        </p>
        {transcription.correction && !brouillon && (
          <button
            type="button"
            onClick={commencer}
            disabled={transcription.correction.refus !== null}
            className={`${CLASSE_BOUTON} border border-gray-200 bg-white text-codialis-navy hover:bg-gray-50`}
          >
            <Pencil className="h-4 w-4" aria-hidden />
            Corriger la transcription
          </button>
        )}
      </div>
      {transcription.correction?.refus && !brouillon && (
        <p className="rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-800">{transcription.correction.refus}</p>
      )}

      <ol
        ref={liste}
        onWheel={noterInteraction}
        onTouchMove={noterInteraction}
        onPointerDown={noterInteraction}
        aria-label="Transcription de la leçon"
        className="scrollbar-thin relative max-h-96 space-y-1 overflow-y-auto pr-2 text-sm"
      >
        {repliques.map((replique, rang) => {
          const lue = rang === courante;
          const horodatage = tempsLisible(replique.debutMs / 1000);
          const surligne = lue ? 'border-l-2 border-codialis-mint bg-codialis-mint/10' : 'border-l-2 border-transparent';
          if (brouillon) {
            const texte = brouillon[rang] ?? '';
            const modifiee = normaliser(texte) !== replique.texte;
            return (
              <li key={rang} className={`flex gap-2 rounded px-2 py-1.5 ${surligne}`}>
                {allerA ? (
                  <button
                    type="button"
                    onClick={() => allerA(replique.debutMs / 1000)}
                    aria-label={`Réécouter le passage de ${horodatage}`}
                    className="mt-1.5 shrink-0 self-start rounded font-mono text-xs text-codialis-sky hover:underline [@media(pointer:coarse)]:min-h-[40px]"
                  >
                    {horodatage}
                  </button>
                ) : (
                  <span className="mt-1.5 shrink-0 font-mono text-xs text-codialis-gray">{horodatage}</span>
                )}
                <div className="min-w-0 flex-1">
                  <textarea
                    value={texte}
                    rows={2}
                    onChange={(evenement) => {
                      const valeur = evenement.target.value;
                      setBrouillon((actuel) => (actuel ? actuel.map((ancien, i) => (i === rang ? valeur : ancien)) : actuel));
                    }}
                    aria-label={`Réplique de ${horodatage}`}
                    className={`w-full resize-y rounded-lg border p-2 text-sm text-codialis-navy focus:outline-none focus:ring-2 focus:ring-codialis-mint ${
                      modifiee ? 'border-codialis-sky' : 'border-gray-200'
                    }`}
                  />
                  {normaliser(texte) === '' && (
                    <p className="text-[11px] text-amber-700">Réplique vide : elle sera retirée à l’enregistrement.</p>
                  )}
                </div>
              </li>
            );
          }
          const contenu = (
            <>
              <span className="mt-0.5 shrink-0 font-mono text-xs text-codialis-gray">{horodatage}</span>
              <span className={lue ? 'font-medium text-codialis-navy' : 'text-codialis-gray'}>{replique.texte}</span>
            </>
          );
          return (
            <li key={rang} aria-current={lue ? 'true' : undefined}>
              {allerA ? (
                <button
                  type="button"
                  onClick={() => allerA(replique.debutMs / 1000)}
                  className={`flex w-full gap-3 rounded px-2 py-1.5 text-left hover:bg-gray-50 ${surligne}`}
                >
                  {contenu}
                </button>
              ) : (
                <div className={`flex gap-3 rounded px-2 py-1.5 ${surligne}`}>{contenu}</div>
              )}
            </li>
          );
        })}
      </ol>

      {brouillon && (
        <div className="sticky bottom-0 flex flex-wrap items-center gap-2 border-t border-gray-100 bg-white pt-3">
          <span className="min-w-0 flex-1 basis-40 text-xs text-codialis-gray">
            {modifiees === 0
              ? 'Aucune réplique modifiée'
              : `${modifiees} réplique${modifiees > 1 ? 's' : ''} modifiée${modifiees > 1 ? 's' : ''}`}
          </span>
          <button
            type="button"
            onClick={() => setBrouillon(null)}
            className={`${CLASSE_BOUTON} border border-gray-200 bg-white text-codialis-navy hover:bg-gray-50`}
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={() => void enregistrer()}
            disabled={modifiees === 0 || corriger.isPending}
            className={`${CLASSE_BOUTON} bg-codialis-navy text-white hover:bg-codialis-deep`}
          >
            {corriger.isPending ? 'Enregistrement…' : 'Enregistrer les corrections'}
          </button>
        </div>
      )}
      {erreurCorrection && (
        <div className="space-y-2">
          <MessageErreur message={erreurCorrection} />
          <button type="button" onClick={() => void recharger()} className="text-sm font-semibold text-codialis-sky hover:underline">
            Recharger la transcription (les corrections non enregistrées sont perdues)
          </button>
        </div>
      )}
    </div>
  );
}
